import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

function CrudList({ endpoint, title, subtitle, testPrefix }) {
    const [items, setItems] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", description: "" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const { data } = await api.get(endpoint);
        setItems(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

    const openCreate = () => { setEditing(null); setForm({ name: "", description: "" }); setShowForm(true); };
    const openEdit = (it) => { setEditing(it); setForm({ name: it.name, description: it.description || "" }); setShowForm(true); };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editing) await api.put(`${endpoint}/${editing.id}`, form);
            else await api.post(endpoint, form);
            toast.success(editing ? "Atualizado" : "Criado");
            setShowForm(false);
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    const doDelete = async (it) => {
        if (!confirm(`Excluir "${it.name}"?`)) return;
        try { await api.delete(`${endpoint}/${it.id}`); toast.success("Excluído"); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div className="p-8">
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.24em] text-neutral-500">{subtitle}</div>
                    <h1 className="mt-1 font-heading font-black text-3xl tracking-tight">{title}</h1>
                </div>
                <button data-testid={`${testPrefix}-new`} onClick={openCreate} className="h-11 px-4 bg-brand-blue text-white font-heading font-bold uppercase tracking-wider text-sm hover:bg-brand-blue-hover flex items-center gap-2">
                    <Plus size={16} /> Novo
                </button>
            </div>

            <div className="mt-6 bg-white border border-neutral-300">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 font-mono text-xs uppercase tracking-widest text-neutral-500">
                        <tr>
                            <th className="text-left p-3">Nome</th>
                            <th className="text-left p-3">Slug</th>
                            <th className="text-left p-3">Descrição</th>
                            <th className="text-right p-3">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? <tr><td colSpan={4} className="p-12 text-center font-mono text-sm text-neutral-500">vazio</td></tr>
                            : items.map((it, i) => (
                                <tr key={it.id} className={`border-t border-neutral-200 ${i % 2 ? "bg-neutral-50/50" : ""}`}>
                                    <td className="p-3 font-medium">{it.name}</td>
                                    <td className="p-3 font-mono text-xs text-neutral-500">{it.slug}</td>
                                    <td className="p-3 text-neutral-600">{it.description || "—"}</td>
                                    <td className="p-3">
                                        <div className="flex justify-end gap-1">
                                            <button data-testid={`${testPrefix}-edit-${it.id}`} onClick={() => openEdit(it)} className="p-2 hover:bg-neutral-200"><Pencil size={14} /></button>
                                            <button data-testid={`${testPrefix}-delete-${it.id}`} onClick={() => doDelete(it)} className="p-2 hover:bg-red-100 text-red-700"><Trash2 size={14} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            {showForm && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
                    <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white border border-neutral-300" data-testid={`${testPrefix}-form`}>
                        <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
                            <div className="font-heading font-bold text-lg">{editing ? "Editar" : "Novo"}</div>
                            <button type="button" onClick={() => setShowForm(false)} className="p-2 hover:bg-neutral-100"><X size={16} /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <label className="block">
                                <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">Nome</span>
                                <input required data-testid={`${testPrefix}-name`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full h-11 px-4 border border-neutral-300 focus:outline-none focus:border-brand-blue" />
                            </label>
                            <label className="block">
                                <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">Descrição</span>
                                <textarea rows={3} data-testid={`${testPrefix}-description`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full px-4 py-2 border border-neutral-300 focus:outline-none focus:border-brand-blue" />
                            </label>
                        </div>
                        <div className="p-4 border-t border-neutral-200 flex justify-end gap-2">
                            <button type="button" onClick={() => setShowForm(false)} className="h-11 px-5 bg-neutral-100 hover:bg-neutral-200 text-sm font-medium">Cancelar</button>
                            <button data-testid={`${testPrefix}-save`} disabled={saving} className="h-11 px-6 bg-brand-blue text-white font-heading font-bold uppercase tracking-wider text-sm hover:bg-brand-blue-hover">
                                {saving ? "salvando…" : "Salvar"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

export default CrudList;
